// frontend/src/components/ReportsPanel.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  CheckCircle2,
  Clock3,
  CreditCard,
  ExternalLink,
  FileText,
  MessageSquare,
  RefreshCw,
  Smartphone,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { getUsageOverview } from '../services/reports.ts';
import type { UsageOverviewResponse } from '../services/reports.ts';
import {
  createBillingSubscription,
  getBillingSubscriptionOptions,
  getBillingSummary,
  syncBillingSummary,
} from '../services/api.ts';
import type {
  BillingPayment,
  BillingSubscriptionClinicOption,
  BillingSubscriptionOptions,
  BillingSubscriptionPlanOption,
  BillingSummary,
} from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { getSafeHttpsUrl } from '../utils/safeUrl.ts';
import { formatCnpj, isValidCnpj, normalizeCnpj } from '../utils/cnpj.ts';
import { isCompanyRegistrationApprovalEnabled } from '../config/featureFlags.ts';
import AsaasAttribution from './billing/AsaasAttribution.tsx';
import './billing/ReportsPanel.css';

interface ReportsPanelProps {
  className?: string;
}

interface UsageItem {
  key: string;
  feature: string;
  used: number;
  limit?: number | null;
  limitLabel: string;
  kind: 'metric' | 'connection' | 'automation';
  icon: LucideIcon;
}

interface PlanFeature {
  key: string;
  label: string;
  value: string;
  icon: LucideIcon;
}

interface PaymentInfo {
  methodLabel: string;
  lastCharge: string;
  nextCharge: string;
}

interface ActivationFormState {
  clinicId: string;
  planId: string;
  value: string;
  billingType: string;
  cycle: string;
  nextDueDate: string;
  endDate: string;
  description: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  contractLegalName: string;
  contractDocument: string;
  contractAddress: string;
  representativeName: string;
  representativeDocument: string;
  representativeRole: string;
}

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'muted';

interface StatusMeta {
  label: string;
  tone: Tone;
}

const PAID_PAYMENT_STATUSES = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'];
const ENTERPRISE_PLAN_NAME = 'ENTERPRISE';
const DEFAULT_ENTERPRISE_PRICE = 1000;

const formatUnknownStatus = (status?: string | null): string => {
  if (!status) return 'Não informado';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getBillingStatusMeta = (status?: string | null): StatusMeta => {
  const normalized = String(status || 'trialing').toLowerCase();
  const map: Record<string, StatusMeta> = {
    trialing: { label: 'Em teste', tone: 'info' },
    active: { label: 'Ativo', tone: 'success' },
    past_due: { label: 'Em atraso', tone: 'warning' },
    suspended: { label: 'Suspenso', tone: 'danger' },
    canceled: { label: 'Cancelado', tone: 'muted' },
  };
  return map[normalized] || { label: formatUnknownStatus(status), tone: 'muted' };
};

const getSubscriptionStatusMeta = (status?: string | null): StatusMeta => {
  const normalized = String(status || '').toUpperCase();
  const map: Record<string, StatusMeta> = {
    ACTIVE: { label: 'Ativa', tone: 'success' },
    INACTIVE: { label: 'Inativa', tone: 'muted' },
    EXPIRED: { label: 'Expirada', tone: 'danger' },
    CANCELLED: { label: 'Cancelada', tone: 'muted' },
    CANCELED: { label: 'Cancelada', tone: 'muted' },
    PENDING: { label: 'Pendente', tone: 'warning' },
  };
  return map[normalized] || { label: status ? formatUnknownStatus(status) : 'Não vinculada', tone: 'muted' };
};

const getContractStatusMeta = (status?: string | null): StatusMeta => {
  const normalized = String(status || '').toLowerCase();
  const map: Record<string, StatusMeta> = {
    draft: { label: 'Em preparação', tone: 'info' },
    sent: { label: 'Enviado para assinatura', tone: 'warning' },
    signed: { label: 'Assinado', tone: 'success' },
    canceled: { label: 'Cancelado', tone: 'muted' },
    failed: { label: 'Falha no envio', tone: 'danger' },
  };
  return map[normalized] || { label: status ? formatUnknownStatus(status) : 'Não gerado', tone: 'muted' };
};

const getPaymentStatusMeta = (status: string): StatusMeta => {
  const normalized = String(status || '').toUpperCase();
  const map: Record<string, StatusMeta> = {
    RECEIVED: { label: 'Paga', tone: 'success' },
    CONFIRMED: { label: 'Paga', tone: 'success' },
    RECEIVED_IN_CASH: { label: 'Paga em dinheiro', tone: 'success' },
    PENDING: { label: 'Em aberto', tone: 'info' },
    OVERDUE: { label: 'Vencida', tone: 'danger' },
    REFUNDED: { label: 'Reembolsada', tone: 'muted' },
    REFUND_REQUESTED: { label: 'Reembolso solicitado', tone: 'warning' },
    CHARGEBACK_REQUESTED: { label: 'Chargeback solicitado', tone: 'danger' },
    CHARGEBACK_DISPUTE: { label: 'Chargeback em disputa', tone: 'danger' },
    AWAITING_CHARGEBACK_REVERSAL: { label: 'Aguardando reversão', tone: 'warning' },
    DUNNING_REQUESTED: { label: 'Em recuperação', tone: 'warning' },
    DUNNING_RECEIVED: { label: 'Recuperada', tone: 'success' },
    AWAITING_RISK_ANALYSIS: { label: 'Em análise', tone: 'info' },
  };
  return map[normalized] || { label: formatUnknownStatus(status), tone: 'muted' };
};

const getBillingTypeLabel = (type?: string | null): string => {
  const normalized = String(type || '').toUpperCase();
  const map: Record<string, string> = {
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de crédito',
    PIX: 'Pix',
    UNDEFINED: 'Perguntar ao cliente',
  };
  return map[normalized] || (type ? formatUnknownStatus(type) : 'Não informado');
};

const isPaidPayment = (payment: BillingPayment): boolean => {
  return PAID_PAYMENT_STATUSES.includes(String(payment.status || '').toUpperCase());
};

const formatPrice = (price?: number | null): string => {
  if (price == null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
};

const isEnterprisePlan = (name?: string | null): boolean => {
  return String(name || '').trim().toUpperCase() === ENTERPRISE_PLAN_NAME;
};

const getPlanDefaultPrice = (plan?: { name?: string | null; price?: number | null } | null): number => {
  if (!plan) return 0;
  if (isEnterprisePlan(plan.name)) return DEFAULT_ENTERPRISE_PRICE;
  return Number(plan.price || 0);
};

const getBillingDisplayPrice = (summary?: BillingSummary | null): number => {
  const subscriptionValue = Number(summary?.asaas?.subscription_value);
  if (summary?.asaas?.subscription_id && Number.isFinite(subscriptionValue) && subscriptionValue > 0) {
    return subscriptionValue;
  }
  return getPlanDefaultPrice(summary?.plan);
};

const parseCurrencyValue = (value: string): number => {
  const cleaned = String(value || '').replace(/[^\d,.-]/g, '').trim();
  if (!cleaned) return Number.NaN;

  if (cleaned.includes(',')) {
    return Number(cleaned.replace(/\./g, '').replace(',', '.'));
  }

  if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    return Number(cleaned.replace(/\./g, ''));
  }

  return Number(cleaned);
};

const formatCurrencyFieldValue = (value: string): string => {
  const parsed = parseCurrencyValue(value);
  return Number.isFinite(parsed) && parsed > 0 ? formatPrice(parsed) : '';
};

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const normalized = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? new Date(`${normalized}T00:00:00`)
    : new Date(normalized);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const toInputDate = (value?: string | null): string => {
  if (!value) return '';
  const normalized = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(normalized) ? normalized.slice(0, 10) : '';
};

const todayInputDate = (): string => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const getDefaultDueDate = (clinic?: BillingSubscriptionClinicOption | null): string => {
  const trialEnd = toInputDate(clinic?.trial_ends_at);
  const today = todayInputDate();
  return trialEnd && trialEnd >= today ? trialEnd : today;
};

const addOneYearInputDate = (value: string): string => {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return '';
  const date = new Date(year, month - 1, day, 12);
  date.setFullYear(date.getFullYear() + 1);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const onlyDigits = (value: string): string => String(value || '').replace(/\D/g, '');

const maskCpf = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
};

const maskCnpj = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
};

// Flag desligada: só dígitos, como antes. Flag ligada: aceita CNPJ alfanumérico.
const normalizeActivationDocument = (value: string): string =>
  isCompanyRegistrationApprovalEnabled() ? normalizeCnpj(value) : onlyDigits(value);

const maskCpfCnpj = (value: string): string => {
  if (!isCompanyRegistrationApprovalEnabled()) {
    const digits = onlyDigits(value);
    return digits.length > 11 ? maskCnpj(digits) : maskCpf(digits);
  }
  const document = normalizeCnpj(value);
  return /[A-Z]/.test(document) || document.length > 11 ? formatCnpj(document) : maskCpf(document);
};

const isValidActivationDocument = (value: string): boolean => {
  if (!isCompanyRegistrationApprovalEnabled()) {
    const digits = onlyDigits(value);
    return digits.length === 11 || digits.length === 14;
  }
  const document = normalizeCnpj(value);
  return /^\d{11}$/.test(document) || isValidCnpj(document);
};

const isValidActivationCpf = (value: string): boolean => {
  return onlyDigits(value).length === 11;
};

const formatDocument = (value?: string | null): string => {
  if (!isCompanyRegistrationApprovalEnabled()) {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 11) return maskCpf(digits);
    if (digits.length === 14) return maskCnpj(digits);
    return value || '-';
  }
  const document = normalizeCnpj(String(value || ''));
  if (/^\d{11}$/.test(document)) {
    return maskCpf(document);
  }
  if (/^[A-Z0-9]{12}\d{2}$/.test(document)) {
    return formatCnpj(document);
  }
  return value || '-';
};

const formatLimit = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return 'Não informado';
  if (typeof value === 'boolean') return value ? 'Incluído' : 'Não incluído';
  if (typeof value === 'number') return value.toLocaleString('pt-BR');
  const textValue = String(value).trim();
  const normalized = textValue.toLowerCase();
  if (['unlimited', 'ilimitado', 'infinite', 'infinito'].includes(normalized)) return 'Ilimitado';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString('pt-BR') : textValue;
};

const formatUsageValue = (row: UsageItem): string => {
  if (row.kind === 'automation') {
    return `${row.used.toLocaleString('pt-BR')} ${row.used === 1 ? 'passo' : 'passos'}`;
  }
  return row.used.toLocaleString('pt-BR');
};

const getErrorMessage = (err: unknown, fallback: string): string => {
  return err instanceof Error && err.message ? err.message : fallback;
};

const getBadgeClasses = (tone: Tone, isDark: boolean) => {
  const baseClasses = 'billing-status px-3 py-1.5 rounded-full text-xs font-medium';
  const toneClasses: Record<Tone, string> = {
    success: 'bg-[var(--billing-success-bg)] text-[var(--billing-success)]',
    warning: isDark ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700',
    danger: isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700',
    info: 'bg-[var(--billing-subtle)] text-[var(--billing-link)]',
    muted: 'bg-[var(--billing-subtle)] text-[var(--billing-secondary)]',
  };

  return `${baseClasses} ${toneClasses[tone]}`;
};

const StatusBadge = ({ meta, isDark }: { meta: StatusMeta; isDark: boolean }) => (
  <span className={getBadgeClasses(meta.tone, isDark)}>{meta.label}</span>
);

const Card = ({
  children,
  className = '',
  isDark,
}: {
  children: React.ReactNode;
  className?: string;
  isDark: boolean;
}) => (
  <section className={`billing-card rounded-2xl border ${isDark ? 'bg-[var(--billing-surface)] border-[var(--billing-border)]' : 'bg-white border-[var(--billing-border)]'} ${className}`}>
    {children}
  </section>
);

const CardHeader = ({
  title,
  description,
  actions,
  isDark,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  isDark: boolean;
}) => (
  <div className={`billing-card-header px-5 py-5 border-[var(--billing-border)]`}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className={`text-base font-medium text-[var(--billing-text)]`}>{title}</h3>
        {description && <p className={`mt-1 text-xs text-[var(--billing-muted)]`}>{description}</p>}
      </div>
      {actions}
    </div>
  </div>
);

const LoadingRows = ({ rows = 3, isDark }: { rows?: number; isDark: boolean }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className={`animate-pulse rounded-xl h-14 ${isDark ? 'bg-[var(--billing-subtle)]' : 'bg-[var(--billing-border)]'}`} />
    ))}
  </div>
);

const StatCard = ({ title, value, detail, tone }: {
  title: string;
  value: string;
  detail: string;
  tone?: Tone;
}) => (
  <div className="billing-stat">
    <dt>{title}</dt>
    <dd className={tone === 'success' ? 'billing-stat-success' : tone === 'danger' ? 'billing-stat-danger' : ''}>{value}</dd>
    <dd className="billing-stat-detail">{detail}</dd>
  </div>
);

const Tabs = ({ value, onChange }: { value: string; onChange: (value: string) => void; isDark: boolean }) => (
  <div className="billing-tabs" aria-label="Seções de plano e faturas">
    {['Assinatura', 'Consumo'].map((option) => (
      <button key={option} type="button" aria-pressed={value === option}
        onClick={() => onChange(option)} className={value === option ? 'billing-button billing-button-primary' : 'billing-button'}>
        {option}
      </button>
    ))}
  </div>
);

const normalizeIncludedFeature = (feature: string): string => {
  const trimmed = feature.trim();
  const key = trimmed.toLowerCase().replace(/[\s_-]+/g, '_');
  const map: Record<string, string> = {
    ai_agent: 'Agente de IA',
    agente_ia: 'Agente de IA',
    agent_ai_integrated: 'Agente de IA integrado',
    ai_audio_send: 'Envio de áudio',
    followup_media_send: 'Mídia em follow-ups',
    response_window: 'Janela de resposta',
    clinicorp_integration: 'Integração Clinicorp',
    google_calendar_integration: 'Integração Google Calendar',
    teams_management: 'Gestão de equipes',
    annotations: 'Anotações',
    tasks_management: 'Gestão de tarefas',
    message_scheduling: 'Mensagens agendadas',
    ads_reports: 'Relatórios de anúncios',
    custom_reports: 'Relatórios personalizados',
    dashboard_funnel: 'Funil no dashboard',
    chat_live: 'Chat ao vivo',
    archive_conversations: 'Arquivar conversas',
    flow_control: 'Controle de fluxos',
    human_ai_mode: 'Modo humano e IA',
    support_dedicated: 'Suporte dedicado',
    whatsapp: 'WhatsApp',
    crm: 'CRM',
    follow_up: 'Follow-up',
    followup: 'Follow-up',
    dashboard: 'Dashboard',
    reports: 'Relatórios',
    relatorios: 'Relatórios',
    campaigns: 'Campanhas',
    campanhas: 'Campanhas',
    scheduling: 'Agendamentos',
    agendamentos: 'Agendamentos',
    google_calendar: 'Google Calendar',
  };

  return map[key] || trimmed;
};

const isFeatureRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const getPlanFeatureSource = (features: unknown): Record<string, unknown> => {
  if (!isFeatureRecord(features)) return {};

  const limits = features.limits;
  if (isFeatureRecord(limits)) {
    return limits;
  }
  return features;
};

const getPlanFeatureList = (features: unknown): string[] => {
  if (!isFeatureRecord(features)) return [];

  const featureList = features.features;
  if (!Array.isArray(featureList)) return [];

  return featureList
    .filter((feature: unknown): feature is string => typeof feature === 'string' && feature.trim().length > 0)
    .map(normalizeIncludedFeature);
};

const ReportsPanel: React.FC<ReportsPanelProps> = ({ className = '' }) => {
  const { isDark } = useTheme();
  const [tab, setTab] = useState('Assinatura');
  const [planFeaturesExpanded, setPlanFeaturesExpanded] = useState(false);
  const [usageOverview, setUsageOverview] = useState<UsageOverviewResponse | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSubmitting, setActivationSubmitting] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [activationOptions, setActivationOptions] = useState<BillingSubscriptionOptions | null>(null);
  const [activationForm, setActivationForm] = useState<ActivationFormState>({
    clinicId: '',
    planId: '',
    value: '',
    billingType: 'UNDEFINED',
    cycle: 'MONTHLY',
    nextDueDate: todayInputDate(),
    endDate: addOneYearInputDate(todayInputDate()),
    description: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    contractLegalName: '',
    contractDocument: '',
    contractAddress: '',
    representativeName: '',
    representativeDocument: '',
    representativeRole: '',
  });

  const loadUsageOverview = useCallback(async () => {
    try {
      setUsageLoading(true);
      const data = await getUsageOverview();
      setUsageOverview(data);
    } catch (err: unknown) {
      setBillingError(getErrorMessage(err, 'Não foi possível carregar o consumo.'));
    } finally {
      setUsageLoading(false);
    }
  }, []);

  const loadBillingSummary = useCallback(async () => {
    try {
      setBillingLoading(true);
      setBillingError(null);
      const data = await getBillingSummary();
      setBillingSummary(data);
    } catch (err: unknown) {
      setBillingError(getErrorMessage(err, 'Não foi possível carregar as faturas.'));
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsageOverview();
    loadBillingSummary();
  }, [loadBillingSummary, loadUsageOverview]);

  const handleSyncBilling = async () => {
    try {
      setBillingLoading(true);
      setBillingError(null);
      setBillingNotice(null);
      const data = await syncBillingSummary();
      setBillingSummary(data);
    } catch (err: unknown) {
      setBillingError(getErrorMessage(err, 'Não foi possível atualizar as faturas.'));
    } finally {
      setBillingLoading(false);
    }
  };

  const buildActivationForm = useCallback((
    options: BillingSubscriptionOptions,
    clinic?: BillingSubscriptionClinicOption
  ): ActivationFormState => {
    const currentClinicId = Number(localStorage.getItem('clinic_id') || billingSummary?.clinic?.id || 0);
    const selectedClinic = clinic
      || options.clinics.find((item) => item.clinic_id === currentClinicId)
      || options.clinics[0];
    const selectedPlan = options.plans.find((item) => item.id === selectedClinic?.plan_id) || options.plans[0];
    const profile = selectedClinic?.contract_profile;
    const billingEmail = profile?.billing_email || options.billing_email || '';
    const billingPhone = profile?.billing_phone || selectedClinic?.billing_phone || '';
    const nextDueDate = getDefaultDueDate(selectedClinic);

    return {
      clinicId: selectedClinic ? String(selectedClinic.clinic_id) : '',
      planId: selectedPlan ? String(selectedPlan.id) : '',
      value: selectedPlan ? formatPrice(getPlanDefaultPrice(selectedPlan)) : '',
      billingType: 'UNDEFINED',
      cycle: 'MONTHLY',
      nextDueDate,
      endDate: addOneYearInputDate(nextDueDate),
      description: selectedPlan && selectedClinic ? `${selectedPlan.name} - ${selectedClinic.name}` : '',
      customerName: profile?.legal_name || selectedClinic?.legal_name || selectedClinic?.name || '',
      customerEmail: billingEmail,
      customerPhone: billingPhone,
      contractLegalName: profile?.legal_name || selectedClinic?.legal_name || selectedClinic?.name || '',
      contractDocument: formatDocument(profile?.document || selectedClinic?.document),
      contractAddress: profile?.address || '',
      representativeName: profile?.representative_name || '',
      representativeDocument: formatDocument(profile?.representative_document),
      representativeRole: profile?.representative_role || 'Sócio administrador',
    };
  }, [billingSummary?.clinic?.id]);

  const loadActivationOptions = useCallback(async () => {
    try {
      setActivationLoading(true);
      setActivationError(null);
      const options = await getBillingSubscriptionOptions();
      setActivationOptions(options);
      setActivationForm(buildActivationForm(options));
    } catch (err: unknown) {
      setActivationError(getErrorMessage(err, 'Não foi possível carregar as opções de assinatura.'));
    } finally {
      setActivationLoading(false);
    }
  }, [buildActivationForm]);

  const openActivationModal = () => {
    setActivationOpen(true);
    setActivationError(null);
    loadActivationOptions();
  };

  const selectedActivationClinic = useMemo(() => {
    return activationOptions?.clinics.find((clinic) => String(clinic.clinic_id) === activationForm.clinicId) || null;
  }, [activationForm.clinicId, activationOptions?.clinics]);

  const activationHasSubscription = Boolean(selectedActivationClinic?.asaas_subscription_id);

  const handleActivationClinicChange = (clinicId: string) => {
    const selectedClinic = activationOptions?.clinics.find((clinic) => String(clinic.clinic_id) === clinicId);
    if (!activationOptions || !selectedClinic) {
      setActivationForm((current) => ({ ...current, clinicId }));
      return;
    }
    setActivationForm(buildActivationForm(activationOptions, selectedClinic));
  };

  const handleActivationPlanChange = (planId: string) => {
    const selectedPlan = activationOptions?.plans.find((planOption) => String(planOption.id) === planId);
    setActivationForm((current) => ({
      ...current,
      planId,
      value: selectedPlan ? formatPrice(getPlanDefaultPrice(selectedPlan)) : current.value,
      description: selectedPlan && selectedActivationClinic
        ? `${selectedPlan.name} - ${selectedActivationClinic.name}`
        : current.description,
    }));
  };

  const handleCreateSubscription = async (event: React.FormEvent) => {
    event.preventDefault();
    setActivationError(null);
    setBillingNotice(null);

    const numericValue = parseCurrencyValue(activationForm.value);
    if (!activationForm.clinicId || !activationForm.planId) {
      setActivationError('Selecione a clínica e o plano da assinatura.');
      return;
    }
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      setActivationError('Informe um valor válido para a assinatura.');
      return;
    }
    if (!activationForm.nextDueDate) {
      setActivationError('Informe a data de início da cobrança.');
      return;
    }
    if (activationForm.cycle !== 'MONTHLY') {
      setActivationError('O plano Enterprise deve ser contratado com cobrança mensal.');
      return;
    }
    if (activationForm.endDate !== addOneYearInputDate(activationForm.nextDueDate)) {
      setActivationError('O contrato Enterprise deve durar 12 meses a partir da data de início.');
      return;
    }
    if (!activationForm.contractLegalName.trim()) {
      setActivationError('Informe o nome ou razão social do contrato.');
      return;
    }
    if (!isValidActivationDocument(activationForm.contractDocument)) {
      setActivationError('Informe um CPF ou CNPJ válido para o contrato.');
      return;
    }
    if (!activationForm.contractAddress.trim()) {
      setActivationError('Informe o endereço completo do contrato.');
      return;
    }
    if (!activationForm.representativeName.trim()) {
      setActivationError('Informe o representante legal.');
      return;
    }
    if (!isValidActivationCpf(activationForm.representativeDocument)) {
      setActivationError('Informe um CPF válido para o representante.');
      return;
    }
    if (!activationForm.representativeRole.trim()) {
      setActivationError('Informe o cargo do representante.');
      return;
    }
    if (!activationForm.customerEmail.trim()) {
      setActivationError('Informe o e-mail financeiro para envio do contrato.');
      return;
    }

    try {
      setActivationSubmitting(true);
      const result = await createBillingSubscription({
        clinic_id: Number(activationForm.clinicId),
        plan_id: Number(activationForm.planId),
        value: numericValue,
        billing_type: activationForm.billingType,
        cycle: activationForm.cycle,
        next_due_date: activationForm.nextDueDate,
        end_date: activationForm.endDate || null,
        description: activationForm.description || null,
        customer_name: activationForm.customerName || null,
        customer_email: activationForm.customerEmail || null,
        customer_phone: activationForm.customerPhone || null,
        contract_profile: {
          legal_name: activationForm.contractLegalName.trim(),
          document: normalizeActivationDocument(activationForm.contractDocument),
          address: activationForm.contractAddress.trim(),
          representative_name: activationForm.representativeName.trim(),
          representative_document: onlyDigits(activationForm.representativeDocument),
          representative_role: activationForm.representativeRole.trim(),
          billing_email: activationForm.customerEmail.trim(),
          billing_phone: activationForm.customerPhone || null,
        },
      });
      setActivationOpen(false);
      if (result.contract_status === 'sent') {
        setBillingNotice(`Assinatura criada e contrato enviado para ${activationForm.customerEmail}.`);
      } else if (result.contract_status === 'failed') {
        setBillingNotice(`Assinatura criada, mas o contrato não foi enviado: ${result.contract_error || 'verifique a integração D4Sign.'}`);
      } else {
        setBillingNotice(`Assinatura criada e vinculada à clínica ID ${result.clinic_id}.`);
      }
      await loadBillingSummary();
    } catch (err: unknown) {
      setActivationError(getErrorMessage(err, 'Não foi possível criar a assinatura.'));
    } finally {
      setActivationSubmitting(false);
    }
  };

  const plan = billingSummary?.plan;
  const planFeatureSource = useMemo(() => getPlanFeatureSource(plan?.features), [plan?.features]);
  const includedFeatures = useMemo(() => getPlanFeatureList(plan?.features), [plan?.features]);

  const getFeatureNumber = useCallback((key: string, fallback: number): number => {
    const value = planFeatureSource[key];
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }, [planFeatureSource]);

  const planName = plan?.name || 'Plano Free';
  const planPrice = billingSummary ? formatPrice(getBillingDisplayPrice(billingSummary)) : 'R$ 0,00';

  const planFeatures: PlanFeature[] = useMemo(() => {
    const knownFeatures: PlanFeature[] = [
      { key: 'messages_per_month', label: 'Mensagens', value: formatLimit(planFeatureSource.messages_per_month), icon: MessageSquare },
      { key: 'leads_per_month', label: 'Leads', value: formatLimit(planFeatureSource.leads_per_month), icon: Users },
      { key: 'contacts_limit', label: 'Contatos', value: formatLimit(planFeatureSource.contacts_limit), icon: Users },
      { key: 'whatsapp_connections', label: 'Conexões WhatsApp', value: formatLimit(planFeatureSource.whatsapp_connections), icon: Smartphone },
      { key: 'ai_credits', label: 'Créditos de IA', value: formatLimit(planFeatureSource.ai_credits), icon: Bot },
      { key: 'campaigns_limit', label: 'Campanhas', value: formatLimit(planFeatureSource.campaigns_limit), icon: FileText },
      { key: 'scheduled_messages_limit', label: 'Mensagens agendadas', value: formatLimit(planFeatureSource.scheduled_messages_limit), icon: Clock3 },
    ].filter((feature) => feature.value !== 'Não informado');
    const includedPlanFeatures: PlanFeature[] = includedFeatures.map((feature, index) => ({
      key: `included-${index}-${feature}`,
      label: feature,
      value: 'Incluído',
      icon: CheckCircle2,
    }));

    if (knownFeatures.length > 0 || includedPlanFeatures.length > 0) {
      return [...knownFeatures, ...includedPlanFeatures];
    }

    return [
      { key: 'empty', label: 'Recursos do plano', value: 'Não informados', icon: CheckCircle2 },
    ];
  }, [includedFeatures, planFeatureSource]);
  const hasPlanFeatures = planFeatures.some((feature) => feature.key !== 'empty');
  const planFeaturesSummary = hasPlanFeatures
    ? `${planFeatures.length} ${planFeatures.length === 1 ? 'recurso' : 'recursos'}`
    : 'Não informados';

  const payments = useMemo(() => {
    return [...(billingSummary?.payments || [])].sort((a, b) =>
      String(b.due_date || '').localeCompare(String(a.due_date || ''))
    );
  }, [billingSummary?.payments]);

  const openPayments = useMemo(() => {
    return payments.filter((payment) => !isPaidPayment(payment));
  }, [payments]);

  const paymentInfo: PaymentInfo = useMemo(() => {
    const paidPayments = payments
      .filter(isPaidPayment)
      .sort((a, b) => String(b.payment_date || '').localeCompare(String(a.payment_date || '')));
    const nextOpen = [...openPayments].sort((a, b) =>
      String(a.due_date || '').localeCompare(String(b.due_date || ''))
    )[0];

    return {
      methodLabel: billingSummary?.asaas?.subscription_id ? 'Assinatura recorrente' : 'Não configurada',
      lastCharge: formatDate(paidPayments[0]?.payment_date),
      nextCharge: formatDate(nextOpen?.due_date),
    };
  }, [billingSummary?.asaas?.subscription_id, openPayments, payments]);

  const usageData: UsageItem[] = useMemo(() => {
    if (!usageOverview) return [];

    const automationIcons: Record<string, LucideIcon> = {
      follow_up: MessageSquare,
      confirmation: CheckCircle2,
      noshow: XCircle,
      pos_consulta: Users,
      pos_venda: CreditCard,
    };
    const whatsappLimit = getFeatureNumber('whatsapp_connections', 1);

    return [
      {
        key: 'messages',
        feature: 'Mensagens',
        used: usageOverview.messages || 0,
        limitLabel: 'Ilimitado',
        kind: 'metric',
        icon: MessageSquare,
      },
      {
        key: 'leads',
        feature: 'Leads',
        used: usageOverview.leads || 0,
        limitLabel: 'Ilimitado',
        kind: 'metric',
        icon: Users,
      },
      {
        key: 'contacts',
        feature: 'Contatos',
        used: usageOverview.contacts || 0,
        limitLabel: 'Ilimitado',
        kind: 'metric',
        icon: Users,
      },
      {
        key: 'whatsapp_connections',
        feature: 'Conexões WhatsApp',
        used: usageOverview.whatsapp_connections || 0,
        limit: whatsappLimit,
        limitLabel: whatsappLimit.toLocaleString('pt-BR'),
        kind: 'connection',
        icon: Smartphone,
      },
      ...(usageOverview.automations || []).map((automation) => ({
        key: automation.key,
        feature: automation.label,
        used: automation.steps || 0,
        limitLabel: 'Sem limite',
        kind: 'automation' as const,
        icon: automationIcons[automation.key] || FileText,
      })),
    ];
  }, [getFeatureNumber, usageOverview]);

  const billingStatus = getBillingStatusMeta(billingSummary?.billing?.status);
  const subscriptionStatus = getSubscriptionStatusMeta(billingSummary?.asaas?.subscription_status);
  const contractStatus = getContractStatusMeta(billingSummary?.contract?.status);
  const hasAsaasSubscription = Boolean(billingSummary?.asaas?.subscription_id);
  const canActivateSubscription = Boolean(billingSummary) && !hasAsaasSubscription;
  const hasBillingLink = Boolean(billingSummary?.asaas?.customer_id || billingSummary?.asaas?.subscription_id);
  const overdueCount = openPayments.filter((payment) => String(payment.status || '').toUpperCase() === 'OVERDUE').length;
  const fieldClasses = `h-11 w-full rounded-xl border px-3 text-sm outline-none transition-colors ${
    isDark
      ? 'border-[var(--billing-border)] bg-[var(--billing-subtle)] text-[var(--billing-text)] focus:border-sminy-primary'
      : 'border-[var(--billing-border)] bg-white text-[var(--billing-text)] focus:border-sminy-primary'
  }`;
  const lockedFieldClasses = `h-11 w-full cursor-not-allowed rounded-xl border px-3 text-sm outline-none ${
    isDark
      ? 'border-[var(--billing-border)] bg-[var(--billing-surface)] text-[var(--billing-muted)]'
      : 'border-[var(--billing-border)] bg-[var(--billing-subtle)] text-[var(--billing-muted)]'
  }`;
  const labelClasses = `text-xs font-medium text-[var(--billing-secondary)]`;

  return (
    <div className={`billing-page ${className}`} data-theme={isDark ? 'dark' : 'light'}>
      <header className="billing-header">
        <div>
          <p className="billing-eyebrow">MINHA CONTA</p>
          <h1>Plano e faturas</h1>
          <p className="billing-subtitle">Acompanhe sua assinatura e seus pagamentos.</p>
          <p className="billing-subtitle">{billingSummary?.clinic?.name || 'Clínica selecionada'}</p>
        </div>
        <div className="billing-header-actions">
          {canActivateSubscription && (
            <button type="button" onClick={openActivationModal} className="billing-button">
              <CreditCard className="h-4 w-4" aria-hidden="true" /> Ativar assinatura
            </button>
          )}
          <button type="button" onClick={handleSyncBilling} disabled={billingLoading} className="billing-button">
            <RefreshCw className={`h-4 w-4 ${billingLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Atualizar faturas
          </button>
        </div>
      </header>

      {activationOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !activationSubmitting && setActivationOpen(false)}
          />
          <form
            onSubmit={handleCreateSubscription}
            className={`relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${
              isDark ? 'border-[var(--billing-border)] bg-[var(--billing-surface)]' : 'border-[var(--billing-border)] bg-white'
            }`}
          >
            <div className={`flex items-start justify-between gap-4 border-b px-5 py-4 border-[var(--billing-border)]`}>
              <div>
                <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[var(--billing-text)]'}`}>Ativar assinatura</h2>
                <p className={`mt-1 text-xs text-[var(--billing-muted)]`}>
                  Crie a cobrança recorrente e vincule ao contrato da clínica.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActivationOpen(false)}
                disabled={activationSubmitting}
                className={`grid h-9 w-9 place-items-center rounded-xl transition-colors disabled:opacity-50 ${
                  isDark ? 'hover:bg-[var(--billing-subtle)] text-[var(--billing-secondary)]' : 'hover:bg-[var(--billing-subtle)] text-[var(--billing-secondary)]'
                }`}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto p-5">
              {activationLoading ? (
                <LoadingRows rows={5} isDark={isDark} />
              ) : (
                <div className="space-y-5">
                  {activationError && (
                    <div className={`rounded-2xl border p-3 ${
                      isDark
                        ? 'border-red-900/60 bg-red-950/30 text-red-300'
                        : 'border-red-200 bg-red-50 text-red-700'
                    }`}>
                      <div className="flex gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <p className="text-sm">{activationError}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-2">
                      <span className={labelClasses}>Clínica</span>
                      <select
                        className={fieldClasses}
                        value={activationForm.clinicId}
                        onChange={(event) => handleActivationClinicChange(event.target.value)}
                        disabled={activationSubmitting || !activationOptions?.clinics.length}
                      >
                        {(activationOptions?.clinics || []).map((clinicOption) => (
                          <option key={clinicOption.clinic_id} value={clinicOption.clinic_id}>
                            {clinicOption.name} - ID {clinicOption.clinic_id}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Plano contratado</span>
                      <select
                        className={fieldClasses}
                        value={activationForm.planId}
                        onChange={(event) => handleActivationPlanChange(event.target.value)}
                        disabled={activationSubmitting || !activationOptions?.plans.length}
                      >
                        {(activationOptions?.plans || []).map((planOption: BillingSubscriptionPlanOption) => (
                          <option key={planOption.id} value={planOption.id}>
                            {planOption.name} - {formatPrice(getPlanDefaultPrice(planOption))}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {selectedActivationClinic && (
                    <div className={`rounded-2xl border p-4 ${isDark ? 'border-[var(--billing-border)] bg-gray-900/40' : 'border-[var(--billing-border)] bg-[var(--billing-subtle)]'}`}>
                      <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                        <div>
                          <p className={`text-xs text-[var(--billing-muted)]`}>Documento</p>
                          <p className={isDark ? 'text-[var(--billing-text)]' : 'text-[var(--billing-text)]'}>{formatDocument(selectedActivationClinic.document)}</p>
                        </div>
                        <div>
                          <p className={`text-xs text-[var(--billing-muted)]`}>Status atual</p>
                          <p className={isDark ? 'text-[var(--billing-text)]' : 'text-[var(--billing-text)]'}>
                            {getBillingStatusMeta(selectedActivationClinic.billing_status).label}
                          </p>
                        </div>
                        <div>
                          <p className={`text-xs text-[var(--billing-muted)]`}>Fim do teste</p>
                          <p className={isDark ? 'text-[var(--billing-text)]' : 'text-[var(--billing-text)]'}>{formatDate(selectedActivationClinic.trial_ends_at)}</p>
                        </div>
                      </div>
                      {activationHasSubscription && (
                        <div className={`mt-3 rounded-xl border p-3 text-sm ${
                          isDark
                            ? 'border-yellow-900/60 bg-yellow-950/30 text-yellow-300'
                            : 'border-yellow-200 bg-yellow-50 text-yellow-800'
                        }`}>
                          Esta clínica já possui uma assinatura vinculada. Sincronize as faturas ou selecione outra clínica.
                        </div>
                      )}
                      {!selectedActivationClinic.asaas_customer_id && !activationHasSubscription && (
                        <div className={`mt-3 rounded-xl border p-3 text-sm ${
                          isDark
                            ? 'border-sminy-primary/40 bg-sminy-primary/10 text-sminy-blue-light'
                            : 'border-sminy-primary/30 bg-sminy-primary/10 text-sminy-primary-hover'
                        }`}>
                          O cliente de cobrança será criado automaticamente com os dados já cadastrados.
                        </div>
                      )}
                    </div>
                  )}

                  <div className={`rounded-2xl border p-4 border-[var(--billing-border)]`}>
                    <div className="mb-4">
                      <h3 className={`text-base font-medium text-[var(--billing-text)]`}>Dados do contrato</h3>
                      <p className={`mt-1 text-xs text-[var(--billing-muted)]`}>
                        Esses dados preenchem o modelo e identificam quem assina pela clínica.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className={labelClasses}>Nome ou razão social</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          value={activationForm.contractLegalName}
                          onChange={(event) => setActivationForm((current) => ({
                            ...current,
                            contractLegalName: event.target.value,
                            customerName: event.target.value,
                          }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>CPF/CNPJ</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          inputMode={isCompanyRegistrationApprovalEnabled() ? 'text' : 'numeric'}
                          autoCapitalize={isCompanyRegistrationApprovalEnabled() ? 'characters' : undefined}
                          spellCheck={isCompanyRegistrationApprovalEnabled() ? false : undefined}
                          value={activationForm.contractDocument}
                          onChange={(event) => setActivationForm((current) => ({
                            ...current,
                            contractDocument: maskCpfCnpj(event.target.value),
                          }))}
                          disabled={activationSubmitting}
                          placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className={labelClasses}>Endereço completo</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          value={activationForm.contractAddress}
                          onChange={(event) => setActivationForm((current) => ({ ...current, contractAddress: event.target.value }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>Representante legal</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          value={activationForm.representativeName}
                          onChange={(event) => setActivationForm((current) => ({ ...current, representativeName: event.target.value }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>CPF do representante</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          inputMode="numeric"
                          value={activationForm.representativeDocument}
                          onChange={(event) => setActivationForm((current) => ({
                            ...current,
                            representativeDocument: maskCpf(event.target.value),
                          }))}
                          disabled={activationSubmitting}
                          placeholder="000.000.000-00"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>Cargo</span>
                        <input
                          className={lockedFieldClasses}
                          type="text"
                          value={activationForm.representativeRole}
                          readOnly
                          disabled
                        />
                      </label>
                    </div>
                  </div>

                  <div className={`rounded-2xl border p-4 border-[var(--billing-border)]`}>
                    <div className="mb-4">
                      <h3 className={`text-base font-medium text-[var(--billing-text)]`}>Dados de cobrança</h3>
                      <p className={`mt-1 text-xs text-[var(--billing-muted)]`}>
                        Revise os dados que serão usados no cliente de cobrança.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <label className="space-y-2">
                        <span className={labelClasses}>Nome</span>
                        <input
                          className={fieldClasses}
                          type="text"
                          value={activationForm.customerName}
                          onChange={(event) => setActivationForm((current) => ({ ...current, customerName: event.target.value }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>E-mail</span>
                        <input
                          className={fieldClasses}
                          type="email"
                          value={activationForm.customerEmail}
                          onChange={(event) => setActivationForm((current) => ({ ...current, customerEmail: event.target.value }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                      <label className="space-y-2">
                        <span className={labelClasses}>Telefone</span>
                        <input
                          className={fieldClasses}
                          type="tel"
                          value={activationForm.customerPhone}
                          onChange={(event) => setActivationForm((current) => ({ ...current, customerPhone: event.target.value }))}
                          disabled={activationSubmitting}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-2">
                      <span className={labelClasses}>Valor</span>
                      <input
                        className={fieldClasses}
                        type="text"
                        inputMode="decimal"
                        value={activationForm.value}
                        onChange={(event) => setActivationForm((current) => ({ ...current, value: event.target.value }))}
                        onBlur={() => setActivationForm((current) => ({
                          ...current,
                          value: formatCurrencyFieldValue(current.value),
                        }))}
                        disabled={activationSubmitting}
                        placeholder="R$ 1.000,00"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Forma de pagamento</span>
                      <select
                        className={fieldClasses}
                        value={activationForm.billingType}
                        onChange={(event) => setActivationForm((current) => ({ ...current, billingType: event.target.value }))}
                        disabled={activationSubmitting}
                      >
                        <option value="UNDEFINED">Perguntar ao cliente</option>
                        <option value="BOLETO">Boleto</option>
                        <option value="PIX">Pix</option>
                        <option value="CREDIT_CARD">Cartão de crédito</option>
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Ciclo</span>
                      <select
                        className={fieldClasses}
                        value={activationForm.cycle}
                        disabled
                      >
                        <option value="MONTHLY">Mensal</option>
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Início da cobrança</span>
                      <input
                        className={fieldClasses}
                        type="date"
                        value={activationForm.nextDueDate}
                        onChange={(event) => setActivationForm((current) => ({
                          ...current,
                          nextDueDate: event.target.value,
                          endDate: addOneYearInputDate(event.target.value),
                        }))}
                        disabled={activationSubmitting}
                      />
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Data final</span>
                      <input
                        className={fieldClasses}
                        type="date"
                        value={activationForm.endDate}
                        disabled
                      />
                    </label>

                    <label className="space-y-2">
                      <span className={labelClasses}>Descrição</span>
                      <input
                        className={fieldClasses}
                        type="text"
                        value={activationForm.description}
                        onChange={(event) => setActivationForm((current) => ({ ...current, description: event.target.value }))}
                        disabled={activationSubmitting}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className={`flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end border-[var(--billing-border)]`}>
              <button
                type="button"
                onClick={() => setActivationOpen(false)}
                disabled={activationSubmitting}
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                  isDark ? 'border-[var(--billing-border)] text-[var(--billing-text)] hover:bg-[var(--billing-subtle)]' : 'border-[var(--billing-border)] text-[var(--billing-secondary)] hover:bg-[var(--billing-subtle)]'
                }`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={activationLoading || activationSubmitting || activationHasSubscription || !activationOptions?.clinics.length || !activationOptions?.plans.length}
                className="rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {activationSubmitting ? 'Criando...' : 'Criar assinatura'}
              </button>
            </div>
          </form>
        </div>
      )}

      <main className="billing-main">
        <Tabs value={tab} onChange={setTab} isDark={isDark} />
        {billingError && (
          <div className={`rounded-2xl border p-4 ${
            isDark
              ? 'border-red-900/60 bg-red-950/30 text-red-300'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}>
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{billingError}</p>
            </div>
          </div>
        )}

        {billingNotice && (
          <div className={`rounded-2xl border p-4 ${
            isDark
              ? 'border-green-900/60 bg-green-950/30 text-green-300'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}>
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{billingNotice}</p>
            </div>
          </div>
        )}

        <dl className="billing-summary" aria-label="Resumo da assinatura" aria-busy={billingLoading}>
          <StatCard title="Status da assinatura" value={billingLoading && !billingSummary ? '—' : billingStatus.label} detail={subscriptionStatus.label} tone={billingStatus.tone} />
          <StatCard title="Próxima fatura" value={paymentInfo.nextCharge} detail={`${openPayments.length} em aberto`} />
          <StatCard title="Último pagamento" value={paymentInfo.lastCharge} detail={paymentInfo.methodLabel} />
          <StatCard title="Faturas vencidas" value={billingLoading && !billingSummary ? '—' : String(overdueCount)} detail={`${payments.length} faturas sincronizadas`} tone={overdueCount > 0 ? 'danger' : undefined} />
        </dl>

        {tab === 'Assinatura' ? (
          <div className="space-y-6">
            <div className="billing-plan-grid">
              <Card isDark={isDark} className="overflow-hidden">
                <CardHeader
                  isDark={isDark}
                  title="Plano contratado"
                  description={hasBillingLink ? 'Cobrança vinculada' : 'Cobrança não configurada'}
                  actions={<StatusBadge meta={billingStatus} isDark={isDark} />}
                />
                <div className="p-4">
                  {billingLoading && !billingSummary ? (
                    <LoadingRows rows={4} isDark={isDark} />
                  ) : (
                    <div className="space-y-5">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h2 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-[var(--billing-text)]'}`}>
                            {planName}
                          </h2>
                          <p className="billing-plan-price">{planPrice}</p>
                        </div>
                      </div>

                      {billingSummary?.billing?.status === 'past_due' && (
                        <div className={`rounded-2xl border p-4 ${
                          isDark
                            ? 'border-yellow-900/60 bg-yellow-950/30 text-yellow-300'
                            : 'border-yellow-200 bg-yellow-50 text-yellow-800'
                        }`}>
                          <div className="flex gap-3">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm">
                              Existe cobrança em atraso. O acesso será suspenso após {billingSummary.billing.grace_days} dias do vencimento.
                            </p>
                          </div>
                        </div>
                      )}

                      {billingSummary?.billing?.status === 'suspended' && (
                        <div className={`rounded-2xl border p-4 ${
                          isDark
                            ? 'border-red-900/60 bg-red-950/30 text-red-300'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}>
                          <div className="flex gap-3">
                            <XCircle className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm">Acesso suspenso por cobrança vencida. Regularize uma fatura para reativar.</p>
                          </div>
                        </div>
                      )}

                      <div className={`border-t pt-4 border-[var(--billing-border)]`}>
                        <button
                          type="button"
                          onClick={() => hasPlanFeatures && setPlanFeaturesExpanded((expanded) => !expanded)}
                          disabled={!hasPlanFeatures}
                          aria-expanded={planFeaturesExpanded}
                          className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
                            hasPlanFeatures
                              ? isDark
                                ? 'hover:bg-[var(--billing-subtle)]'
                                : 'hover:bg-[var(--billing-subtle)]'
                              : 'cursor-default'
                          }`}
                        >
                          <div className="min-w-0">
                            <h3 className={`text-base font-medium text-[var(--billing-text)]`}>
                              Recursos do plano
                            </h3>
                            <p className={`mt-0.5 text-xs ${isDark ? 'text-[var(--billing-muted)]' : 'text-[var(--billing-secondary)]'}`}>
                              {planFeaturesSummary}
                            </p>
                          </div>
                          {hasPlanFeatures && (
                            <ChevronDown
                              className={`h-5 w-5 flex-shrink-0 text-sminy-primary transition-transform ${
                                planFeaturesExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          )}
                        </button>

                        {planFeaturesExpanded && hasPlanFeatures && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {planFeatures.map((feature) => {
                              const Icon = feature.icon;
                              return (
                                <div
                                  key={feature.key}
                                  className={`flex items-center gap-3 rounded-xl border p-3 ${
                                    isDark ? 'border-[var(--billing-border)] bg-[var(--billing-subtle)]' : 'border-[var(--billing-border)] bg-[var(--billing-subtle)]'
                                  }`}
                                >
                                  <div className={`grid h-9 w-9 place-items-center rounded-xl ${
                                    isDark ? 'bg-sminy-primary/20' : 'bg-sminy-primary/10'
                                  }`}>
                                    <Icon className="w-4 h-4 text-sminy-primary" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className={`text-sm font-medium text-[var(--billing-text)]`}>{feature.label}</p>
                                    <p className={`text-xs ${isDark ? 'text-[var(--billing-muted)]' : 'text-[var(--billing-secondary)]'}`}>{feature.value}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              <Card isDark={isDark} className="overflow-hidden">
                <CardHeader
                  isDark={isDark}
                  title="Dados da cobrança"
                  actions={<StatusBadge meta={subscriptionStatus} isDark={isDark} />}
                />
                <div className="p-4">
                  {billingLoading && !billingSummary ? (
                    <LoadingRows rows={4} isDark={isDark} />
                  ) : (
                    <div className="space-y-4 text-sm">
                      {[
                        ['Método', paymentInfo.methodLabel],
                        ['Tipo', getBillingTypeLabel(billingSummary?.asaas?.subscription_billing_type)],
                        ['Contrato', contractStatus.label],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className={`flex items-center justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0 ${
                            isDark ? 'border-[var(--billing-border)]' : 'border-[var(--billing-border)]'
                          }`}
                        >
                          <span className={isDark ? 'text-[var(--billing-muted)]' : 'text-[var(--billing-secondary)]'}>{label}</span>
                          <span className={`font-medium text-right text-[var(--billing-text)]`}>{value}</span>
                        </div>
                      ))}
                      {billingSummary?.contract?.status === 'failed' && billingSummary.contract.last_error && (
                        <div className={`rounded-xl border p-3 text-xs ${
                          isDark
                            ? 'border-red-900/60 bg-red-950/30 text-red-300'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}>
                          {billingSummary.contract.last_error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <Card isDark={isDark} className="billing-invoice-card overflow-hidden">
              <CardHeader
                isDark={isDark}
                title="Faturas"
                description={`${payments.length} registros sincronizados`}
              />
              <div className="p-4">
                {billingLoading && !billingSummary ? (
                  <LoadingRows rows={4} isDark={isDark} />
                ) : payments.length ? (
                  <div className={`rounded-2xl border overflow-hidden border-[var(--billing-border)]`}>
                    <div className="overflow-x-auto">
                      <table className="billing-invoices" aria-label="Faturas">
                        <thead className={isDark ? 'bg-[var(--billing-surface)]' : 'bg-[var(--billing-subtle)]'}>
                          <tr>
                            {['Fatura', 'Vencimento', 'Pagamento', 'Tipo', 'Valor', 'Status', 'Ação'].map((header) => (
                              <th
                                key={header}
                                className={`px-4 py-3 text-xs font-medium uppercase tracking-wider ${
                                  header === 'Ação' ? 'text-right' : 'text-left'
                                } text-[var(--billing-muted)]`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className={`divide-y divide-[var(--billing-border)]`}>
                          {payments.map((payment, index) => {
                            const paymentStatus = getPaymentStatusMeta(payment.status);
                            const paid = isPaidPayment(payment);
                            const rawPaymentLink = paid
                              ? payment.invoice_url || payment.payment_url || payment.bank_slip_url
                              : payment.payment_url || payment.invoice_url || payment.bank_slip_url;
                            const paymentLink = getSafeHttpsUrl(rawPaymentLink);

                            return (
                              <tr
                                key={payment.asaas_payment_id}
                                className={`transition-colors hover:bg-[var(--billing-subtle)]`}
                              >
                                <td data-label="Fatura" className={`px-4 py-3 text-[var(--billing-text)]`}>
                                  <p className="billing-invoice-description text-sm font-medium">{payment.description || `Fatura #${payments.length - index}`}</p>
                                </td>
                                <td data-label="Vencimento" className={`px-4 py-3 text-sm text-[var(--billing-secondary)]`}>{formatDate(payment.due_date)}</td>
                                <td data-label="Pagamento" className={`px-4 py-3 text-sm text-[var(--billing-secondary)]`}>{formatDate(payment.payment_date)}</td>
                                <td data-label="Método" className={`px-4 py-3 text-sm text-[var(--billing-secondary)]`}>{getBillingTypeLabel(payment.billing_type)}</td>
                                <td data-label="Valor" className={`px-4 py-3 text-sm font-medium text-[var(--billing-text)]`}>{formatPrice(payment.value)}</td>
                                <td data-label="Status" className="px-4 py-3">
                                  <StatusBadge meta={paymentStatus} isDark={isDark} />
                                </td>
                                <td data-label="Ação" className="px-4 py-3 text-right">
                                  {paymentLink ? (
                                    <a
                                      href={paymentLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
                                        paid
                                          ? isDark
                                            ? 'border border-[var(--billing-border)] bg-[var(--billing-subtle)] text-[var(--billing-text)] hover:bg-[var(--billing-subtle)]'
                                            : 'border border-[var(--billing-border)] bg-white text-[var(--billing-secondary)] hover:bg-[var(--billing-subtle)]'
                                          : 'bg-sminy-primary text-white hover:bg-sminy-primary/90'
                                      }`}
                                    >
                                      {paid ? 'Ver fatura' : 'Pagar'}
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  ) : (
                                    <span className={`text-xs text-[var(--billing-muted)]`}>-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                      isDark ? 'bg-[var(--billing-subtle)]' : 'bg-[var(--billing-subtle)]'
                    }`}>
                      <FileText className={`w-8 h-8 text-[var(--billing-muted)]`} />
                    </div>
                    <h3 className={`text-lg font-semibold mb-2 text-[var(--billing-text)]`}>
                      Nenhuma fatura encontrada
                    </h3>
                    <p className={`text-sm text-center max-w-sm ${isDark ? 'text-[var(--billing-muted)]' : 'text-[var(--billing-secondary)]'}`}>
                      As faturas sincronizadas aparecem aqui.
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <Card isDark={isDark} className="billing-usage-card overflow-hidden">
            <CardHeader
              isDark={isDark}
              title="Consumo"
              description="Histórico acumulado"
            />
            <div className="p-4">
              {usageLoading ? (
                <LoadingRows rows={6} isDark={isDark} />
                ) : usageData.length ? (
                <div className={`rounded-2xl border overflow-hidden border-[var(--billing-border)]`}>
                  <div className="overflow-x-auto">
                    <table className="billing-usage" aria-label="Consumo histórico acumulado">
                      <thead className={isDark ? 'bg-[var(--billing-surface)]' : 'bg-[var(--billing-subtle)]'}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--billing-muted)]`}>Recurso</th>
                          <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--billing-muted)]`}>Consumo</th>
                          <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--billing-muted)]`}>Limite</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y divide-[var(--billing-border)]`}>
                        {usageData.map((row) => {
                          const Icon = row.icon;

                          return (
                            <tr key={row.key} className={`transition-colors hover:bg-[var(--billing-subtle)]`}>
                              <td data-label="Recurso" className={`px-4 py-3 text-[var(--billing-text)]`}>
                                <div className="flex items-center gap-3">
                                  <div className={`grid h-9 w-9 place-items-center rounded-xl ${
                                    isDark ? 'bg-sminy-primary/20' : 'bg-sminy-primary/10'
                                  }`}>
                                    <Icon className="w-4 h-4 text-sminy-primary" />
                                  </div>
                                  <span className="text-sm font-medium">{row.feature}</span>
                                </div>
                              </td>
                              <td data-label="Consumo" className={`px-4 py-3 text-right text-sm text-[var(--billing-secondary)]`}>{formatUsageValue(row)}</td>
                              <td data-label="Limite" className={`px-4 py-3 text-right text-sm text-[var(--billing-secondary)]`}>{row.limitLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                    isDark ? 'bg-[var(--billing-subtle)]' : 'bg-[var(--billing-subtle)]'
                  }`}>
                    <MessageSquare className={`w-8 h-8 text-[var(--billing-muted)]`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 text-[var(--billing-text)]`}>
                    Consumo indisponível
                  </h3>
                  <p className={`text-sm text-center max-w-sm ${isDark ? 'text-[var(--billing-muted)]' : 'text-[var(--billing-secondary)]'}`}>
                    Os dados de uso aparecem aqui quando o relatório estiver disponível.
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}
        <AsaasAttribution />
      </main>
    </div>
  );
};

export default ReportsPanel;
